import {
  CellClassParams,
  CellStyle,
  ColDef,
  GridOptions,
  ModelUpdatedEvent,
} from '@ag-grid-community/core'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  heliosClientSideRowConfig,
  heliosDefaultColDef,
  heliosMultiRowSelectionConfig,
} from '@helios/ag-grid-config'
import { HighlightingService } from '@helios/generic-components'
import {
  ExternalFileFilter,
  ExternalFileLinkedPanel,
} from '@helios/state-management'
import {
  DensityLevel,
  MemoryStorageService,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { AgGridApi, NfColumnTypes } from '@northfork/ag-grid'
import { composeFieldPath, formatDate } from '@northfork/basics'
import { Daterange, ToastService } from '@northfork/ng-basics'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { ExternalFileViewerMainGridKey } from '../../panel-key'
import {
  contextMenuItems,
  generateUniqueFileKey,
} from '../external-file-viewer.utils'
import {
  Account,
  ExternalRawData,
  ExternalRawDataModel,
  SleeveAggAccount,
} from '../models/external-file-viewer'
import { ExternalFileViewerService } from '../services/external-file-viewer.service'
import { FileDataModel } from './../models/external-file-viewer'

@Component({
  selector: 'hs-external-file-viewer-grid',
  templateUrl: './external-file-viewer-grid.component.html',
  styleUrls: ['./external-file-viewer-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HighlightingService],
})
export class ExternalFileViewerGridComponent implements OnDestroy {
  constructor(
    readonly externalFileViewerService: ExternalFileViewerService,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly highlightService: HighlightingService,
    private readonly memoryStorageService: MemoryStorageService,
    private readonly toastService: ToastService,
  ) {}

  @Input('updatedRawDataSelectedList') rawDataSelected: FileDataModel[] = []
  @Input('filterState') savedFilterState: Record<string, unknown> | null = null
  @Output()
  rawDataSelectedList$ = new EventEmitter<FileDataModel>()
  @Output()
  selectDateRangeFilter = new EventEmitter<Daterange>()

  _accountId?: number
  _accountType?: string
  externalRawData: ExternalRawDataModel[] = []
  aggAccountList: SleeveAggAccount[] = []
  isExternalFileLoading = false
  isSubAccountsLoading = false
  selectedAccount: number[] = []
  required = true
  gridApi?: AgGridApi
  dateRange: Daterange = {
    beginDate: null,
    endDate: null,
  }

  minDate = '1900-01-01'
  maxDate = '2100-12-31'

  private readonly destroySubject$ = new Subject<void>()

  @Input() set dateRangeFilter(dateRange: Daterange | undefined) {
    if (dateRange?.beginDate && dateRange.endDate) {
      this.dateRange = { ...dateRange }
    }
  }

  @Input() set account(value: Account | undefined) {
    if (value) {
      this._accountId = value.accountId
      this.selectedAccount = [value.accountId]
      this.aggAccountList = []
      if (this.dateRange.beginDate && this.dateRange.endDate) {
        this.fetchExternalRawData(
          [value.accountId],
          this.dateRange.beginDate,
          this.dateRange.endDate,
        )
      }
      if ('sleeveAggregate' == value.accountType) {
        this.loadSleeveAggAccounts(value.accountId)
      }
    }
  }
  get accountId() {
    return this._accountId
  }

  @Input() set rawDataFilterData(filter: ExternalFileFilter | undefined) {
    if (!filter) return
    this.resetFilters()
    this._accountId = filter.accountId
    if (filter?.panelName != ExternalFileLinkedPanel.CW_TRANSACTION) {
      this.isExternalFileLoading = true
      this.externalFileViewerService
        .fetchRawDataForExtTransaction(
          filter.id,
          filter.accountId,
          filter.reconDate,
        )
        .subscribe(rawData => {
          this.loadRawFilterData(rawData, filter.filterText)
        })
    } else {
      this.isExternalFileLoading = true
      this.externalFileViewerService
        .fetchRawDataForCwTransaction(filter.id)
        .subscribe(rawData => {
          this.loadRawFilterData(rawData, filter.filterText)
        })
    }
  }

  loadRawFilterData(rawData: ExternalRawData[], filterText: string) {
    const filteredRawData = this.externalRawDataWrapper(rawData)
    this.rawDataSelected = []
    this.isExternalFileLoading = false
    filteredRawData.forEach(data => this.viewSelectedFile(data, filterText))
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
  }

  loadSleeveAggAccounts(accountId: number) {
    this.isSubAccountsLoading = true
    this.externalFileViewerService
      .fetchSubAccounts(accountId)
      .subscribe(accounts => {
        const parentAccount = accounts.findIndex(
          account => account.id == accountId,
        )
        accounts.push(...accounts.splice(0, parentAccount))
        this.aggAccountList = accounts
        this.selectedAccount = [this.aggAccountList[0].id]
        this.selectAllForDropdownItems(this.aggAccountList)
        this.isSubAccountsLoading = false
      })
  }

  fetchExternalRawData(
    accountIds: number[],
    reconStartDate?: string,
    reconEndDate?: string,
  ) {
    if (reconStartDate && reconEndDate) {
      this.isExternalFileLoading = true
      this.externalFileViewerService
        .fetchRawData(reconStartDate, reconEndDate, accountIds)
        .subscribe(
          rawData => {
            this.loadExternalRawData(rawData)
            this.isExternalFileLoading = false
          },
          error => {
            this.isExternalFileLoading = false
            this.toastService.error(
              'Failed to fetch raw data. Error : ' + error,
            )
          },
        )
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectAllForDropdownItems(items: any[]) {
    items.forEach(element => {
      element['selectedAllGroup'] = 'selectedAllGroup'
    })
  }

  resetFilters() {
    this.aggAccountList = []
  }

  applyDateRange(nfDateRange: Daterange) {
    if (this.selectedAccount && nfDateRange.beginDate && nfDateRange.endDate) {
      this.dateRange = {
        beginDate: nfDateRange.beginDate,
        endDate: nfDateRange.endDate,
      }
      this.selectDateRangeFilter.emit(this.dateRange)
      this.fetchExternalRawData(
        this.selectedAccount,
        nfDateRange.beginDate,
        nfDateRange.endDate,
      )
    }
  }

  loadSubAccountsFileData() {
    if (this.selectedAccount.length < 1) {
      return
    }

    if (
      this.selectedAccount &&
      this.dateRange.beginDate &&
      this.dateRange.endDate
    ) {
      this.fetchExternalRawData(
        this.selectedAccount,
        this.dateRange.beginDate,
        this.dateRange.endDate,
      )
    }
  }

  fileViewerField = composeFieldPath(this.externalRawData)

  loadExternalRawData(externalRawData: ExternalRawData[]) {
    this.externalRawData = this.externalRawDataWrapper(externalRawData)
  }

  externalRawDataWrapper(externalRawData: ExternalRawData[]) {
    const rowExternalRawData: ExternalRawDataModel[] = []
    externalRawData.forEach((action: ExternalRawData) => {
      rowExternalRawData.push({
        accountId: action.accountId,
        reconDate: action.endReconDate
          ? `${
              formatDate(action.reconDate) +
              ' - ' +
              formatDate(action.endReconDate)
            }`
          : action.reconDate,
        fileType: action.fileType,
        fileDescription: action.fileDescription,
        totalRows: action.totalRows,
        fileDate: action.reconDate,
        fileId: action.fileId,
        startReconDate: action.reconDate,
        endReconDate: action.endReconDate,
      })
    })
    return rowExternalRawData
  }

  gridOptions: GridOptions = {
    ...heliosClientSideRowConfig,
    ...heliosMultiRowSelectionConfig,
    defaultColDef: {
      ...heliosDefaultColDef,
      sortable: true,
      cellStyle: (params: CellClassParams) =>
        this.highlightService.getCellStyle(
          params.node.id ?? '',
          params.colDef.field ?? '',
        ) as CellStyle,
    },
    sideBar: false,
    columnDefs: this.createColumnDefs(),
    getRowClass: params =>
      this.highlightService.getRowClass(params.node.id ?? ''),
    getRowNodeId: (extRawData: ExternalRawData) =>
      generateUniqueFileKey(extRawData),
    getContextMenuItems: params =>
      contextMenuItems(
        this.highlightService.createHighlightMenuItems(params),
        params,
      ),
    onSelectionChanged: event => {
      const externalRawData = event.api
        .getSelectedNodes()
        .map(x => x.data as ExternalRawData)

      this.viewSelectedFile(externalRawData[0])
      this.gridApi = event as AgGridApi
    },
    onGridReady: params => {
      this.gridApi = params
      this.preferences
        .getDensityLevel()
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(level =>
          enableHeliosGridDensityLevel(
            params.api,
            DensityLevel.Compact === level,
          ),
        )
      this.savedViewService.manageSavedView(
        params,
        ExternalFileViewerMainGridKey,
        this.destroySubject$,
      )
      enableHeliosMultiRowDragSelection(params.api)
      enableHeliosCopyBehavior(params.api)
    },
    onModelUpdated: (gridApi: ModelUpdatedEvent) => {
      if (
        gridApi.newData &&
        this.savedFilterState &&
        !gridApi.api.isAnyFilterPresent()
      ) {
        // Apply preserved filters once grid is rendered
        gridApi.api.setFilterModel(this.savedFilterState)
        setTimeout(() => {
          gridApi.api.onFilterChanged()
        })
      }
    },
    onFirstDataRendered: async event => {
      await this.highlightService.setGridKeyForPersistentStorage(
        ExternalFileViewerMainGridKey,
        this.memoryStorageService,
        event.api,
      )
    },
  }

  viewSelectedFile(externalRawData: ExternalRawData, filterText?: string) {
    this.rawDataSelectedList$.emit({
      fileId: externalRawData.fileId,
      reconDate: externalRawData.reconDate,
      fileType: externalRawData.fileType,
      fileDescription: externalRawData.fileDescription,
      startReconDate: externalRawData.startReconDate,
      endReconDate: externalRawData.endReconDate,
      accountId: externalRawData.accountId,
      filterText: filterText,
    })
  }

  private createColumnDefs(): ColDef[] {
    const colDef = [
      {
        field: this.fileViewerField('accountId'),
        headerName: 'Account ID',
      },
      {
        field: this.fileViewerField('reconDate'),
        type: NfColumnTypes.Date,
      },
      {
        field: this.fileViewerField('fileType'),
      },

      {
        field: this.fileViewerField('fileDate'),
        type: NfColumnTypes.Date,
      },
      {
        field: this.fileViewerField('totalRows'),
      },
      {
        field: this.fileViewerField('fileDescription'),
      },
    ]
    return colDef
  }
}
